/**
 * AlpineJS component for Stripe payment form.
 * Uses a state machine pattern for UI states.
 *
 * States:
 * - 'idle': Initial state before initialization
 * - 'initializing': Loading Stripe.js
 * - 'ready': Form ready for input
 * - 'submitting': Processing payment
 * - 'success': Payment completed successfully
 * - 'error': Initialization or critical error occurred
 */

// Global promise to track Stripe.js loading state (prevents multiple loads)
let stripeLoadingPromise = null;

/**
 * Load Stripe.js from CDN (singleton pattern).
 */
function loadStripeJs() {
    if (window.Stripe) {
        return Promise.resolve();
    }

    if (stripeLoadingPromise) {
        return stripeLoadingPromise;
    }

    stripeLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            stripeLoadingPromise = null;
            reject(new Error('Failed to load Stripe.js'));
        };
        document.head.appendChild(script);
    });

    return stripeLoadingPromise;
}

async function sha256Hex(input) {
    const data = new TextEncoder().encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function computeProofOfWork(billNumber, amount, difficulty) {
    const ts = Math.floor(Date.now() / 1000);
    if (!difficulty || difficulty <= 0) {
        return { ts, nonce: 0, hash: '' };
    }

    const prefix = '0'.repeat(difficulty);
    let nonce = 0;

    while (true) {
        const input = `${ts}|${billNumber}|${amount}|${nonce}`;
        const hash = await sha256Hex(input);
        if (hash.startsWith(prefix)) {
            return { ts, nonce, hash };
        }
        nonce += 1;
        if (nonce % 500 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
        }
    }
}

window.stripePaymentForm = function (translations, apiEndpoint, stripePublicKey, powDifficulty) {
    return {
        // Stripe instances
        stripe: null,
        cardElement: null,

        // Translations from Fusion
        t: translations,

        // Form data
        cardholderName: '',
        email: '',
        phone: '',
        customerNumber: '',
        billNumber: '',
        amount: '',

        // State machine: 'idle' | 'initializing' | 'ready' | 'submitting' | 'success' | 'error'
        state: 'idle',

        // Error message for display
        errorMessage: '',

        /**
         * Initialize the component: load Stripe.js and mount the card element.
         */
        async init() {
            this.state = 'initializing';
            const container = this.$el;

            if (!stripePublicKey) {
                console.error('Stripe public key not found');
                this.state = 'error';
                this.errorMessage = this.t.configError || 'Configuration error: Stripe public key missing.';
                return;
            }

            try {
                await loadStripeJs();

                this.stripe = window.Stripe(stripePublicKey);
                const elements = this.stripe.elements({ locale: 'de' });

                this.cardElement = elements.create('card', {
                    style: {
                        base: {
                            fontSize: '16px',
                            color: '#1a1a1a',
                            '::placeholder': { color: '#9ca3af' },
                        },
                        invalid: { color: '#dc2626' },
                    },
                });

                // Clear and mount card element
                const cardContainer = this.$refs.cardElement;
                if (cardContainer) {
                    cardContainer.innerHTML = '';
                    this.cardElement.mount(cardContainer);
                }

                // Handle card validation errors
                this.cardElement.on('change', (event) => {
                    const errorDisplay = this.$refs.cardErrors;
                    if (errorDisplay) {
                        errorDisplay.textContent = event.error ? event.error.message : '';
                    }
                });

                this.state = 'ready';
                console.log('Stripe form initialized successfully');
            } catch (error) {
                console.error('Failed to initialize Stripe:', error);
                this.state = 'error';
                this.errorMessage = this.t.loadError || 'Payment form could not be loaded.';
            }
        },

        /**
         * Format amount on blur: normalize to 2 decimal places.
         */
        formatAmount() {
            if (!this.amount) return;

            let normalized = this.amount.replace(',', '.').replace(/\s/g, '');
            let num = parseFloat(normalized);

            if (!isNaN(num) && num > 0) {
                this.amount = num.toFixed(2).replace('.', ',');
            }
        },

        /**
         * Handle form submission.
         */
        async handleSubmit() {
            // Only submit from ready state
            if (this.state !== 'ready') {
                console.log('Cannot submit, current state:', this.state);
                return;
            }

            this.errorMessage = '';
            if (this.$refs.cardErrors) {
                this.$refs.cardErrors.textContent = '';
            }

            // Validate required fields
            if (!this.billNumber.trim()) {
                this.errorMessage = this.t.billNumberRequired || 'Please enter the invoice number.';
                return;
            }

            if (!this.amount.trim()) {
                this.errorMessage = this.t.amountRequired || 'Please enter the amount.';
                return;
            }

            const normalizedAmount = this.amount.replace(',', '.').replace(/\s/g, '');
            const numericAmount = parseFloat(normalizedAmount);

            if (isNaN(numericAmount) || numericAmount <= 0) {
                this.errorMessage = this.t.amountInvalid || 'Please enter a valid amount.';
                return;
            }

            // Stripe requires a minimum of €0.50
            if (numericAmount < 0.50) {
                this.errorMessage = this.t.amountMinimum || 'Amount must be at least €0.50.';
                return;
            }

            // Transition to submitting state
            this.state = 'submitting';

            try {
                const container = this.$el;
                if (!window.crypto || !window.crypto.subtle) {
                    this.errorMessage = this.t.genericError || 'An error occurred. Please try again.';
                    this.state = 'ready';
                    return;
                }

                const pow = await computeProofOfWork(
                    this.billNumber.trim(),
                    normalizedAmount,
                    powDifficulty
                );

                // Step 1: Create PaymentIntent via backend
                const language = document.documentElement.lang || 'en';
                const response = await fetch(apiEndpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        billNumber: this.billNumber.trim(),
                        amount: normalizedAmount,
                        customerNumber: this.customerNumber.trim() || null,
                        email: this.email.trim() || null,
                        powTimestamp: pow.ts,
                        powNonce: pow.nonce,
                        language: language,
                    }),
                });

                const data = await response.json();

                if (data.error) {
                    // Server-side errors are already translated
                    this.errorMessage = data.error;
                    this.state = 'ready';
                    return;
                }

                if (!data.clientSecret) {
                    this.errorMessage = this.t.paymentStartFailed || 'Payment could not be started.';
                    this.state = 'ready';
                    return;
                }

                // Step 2: Confirm payment with Stripe.js
                const result = await this.stripe.confirmCardPayment(data.clientSecret, {
                    payment_method: {
                        card: this.cardElement,
                        billing_details: {
                            name: this.cardholderName.trim() || null,
                            email: this.email.trim() || null,
                            phone: this.phone.trim() || null,
                        },
                    },
                });

                if (result.error) {
                    // Stripe errors are already localized by Stripe.js
                    if (this.$refs.cardErrors) {
                        this.$refs.cardErrors.textContent = result.error.message;
                    }
                    this.state = 'ready';
                } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
                    this.state = 'success';
                } else {
                    this.errorMessage = this.t.paymentFailed || 'Payment could not be completed.';
                    this.state = 'ready';
                }
            } catch (error) {
                console.error('Payment error:', error);
                this.errorMessage = this.t.genericError || 'An error occurred. Please try again.';
                this.state = 'ready';
            }
        },
    };
};
